import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { NewButton as Button } from 'components/Button/NewButton';
import { Avatar, AVATAR_HOVER_ICON, AVATAR_SIZE } from 'components/Image';
import { ColorDecoration } from 'components/Misc';
import { Inline } from 'components/Typography';
import { MatchMedia, VisuallyHidden } from 'components/Utilities';

import { COLOR } from 'utilsTS/color';
import { deprecate } from 'utilsTS/error';
import { ICON } from 'utilsTS/icon';
import { elementOfType, requiredIf } from 'utilsTS/prop-types';
import { omitProps, cloneElement } from 'utilsTS/react';

export enum PRODUCT_INFO_ALIGNMENT {
    LEFT = 'left',
    RIGHT = 'right',
}

export enum PRODUCT_INFO_VERBOSITY {
    BRIEF = 'brief',
    REGULAR = 'regular',
}

export interface ProductInfoProps extends React.ComponentPropsWithoutRef<'span'> {
    children?: never;
    /** Defines alias text (primary, shown in bold) */
    alias?: React.ReactNode;
    /** Defines a11y label for the alias text */
    aliasLabel?: string;
    /** Defines the position of the Avatar */
    alignment?: PRODUCT_INFO_ALIGNMENT;
    /** Accepts Amount component to be shown */
    amount?: React.ReactNode;
    /** Defines a11y label for the Amount */
    amountLabel?: string;
    /** Defines visible text below the Amount */
    amountInfo?: React.ReactNode;
    /** Defines a11y label for the visible text below the Amount */
    amountInfoLabel?: string;
    /** Accepts Avatar component to be shown */
    avatar?: React.ReactNode;
    /** Defines back button action to be shown next to the Avatar */
    backButton?: React.ReactNode;
    /** Defines badge colour for the Avatar component
     * @deprecated use `avatar` prop instead
     */
    badgeColor?: COLOR;
    /** Defines badge icon for the Avatar component
     * @deprecated use `avatar` prop instead
     */
    badgeIcon?: ICON;
    /** Accepts Badge components to be shown */
    badges?: React.ReactElement | React.ReactElement[];
    /** Defines a11y label for the Badges components */
    badgesLabel?: string;
    /** Defines decoration color */
    color?: COLOR;
    /** Arranges ProductInfo vertically from top to bottom, like a stack */
    stack?: boolean;
    /** Defines whether ProductInfo should stack horizontally */
    horizontal?: boolean;
    /** Defines Avatar hover icon (shown over the center on hover)
     * @deprecated
     */
    hoverIcon?: AVATAR_HOVER_ICON;
    /** Defines a11y label of the hover icon
     * @deprecated
     */
    hoverIconLabel?: string;
    /** Accepts additional props for the hover icon
     * @deprecated
     */
    hoverIconProps?: React.HTMLAttributes<HTMLButtonElement>;
    /** Defines icon for the Avatar component
     * @deprecated use `avatar` prop instead
     */
    icon?: ICON;
    /** Defines image URL for the Avatar component
     * @deprecated use `avatar` prop instead
     */
    imageUrl?: string;
    /** Defines product number text */
    productNumber?: React.ReactNode;
    /** Defines a11y label for the product number text */
    productNumberLabel?: string;
    /** Defines size for the Avatar component
     * @deprecated use `avatar` prop instead
     */
    size?: AVATAR_SIZE;
    /** Defines whether the alias, productNumber and type should be truncated */
    truncate?: boolean;
    /** Defines type text (secondary, shown above alias) */
    type?: React.ReactNode;
    /** Defines a11y label for the type text */
    typeLabel?: string;
    /** Defines the product verbosity  */
    verbosity?: PRODUCT_INFO_VERBOSITY;
}

export interface ProductInfoStatics {
    ALIGNMENT: typeof PRODUCT_INFO_ALIGNMENT;
    BADGE_COLOR: typeof COLOR;
    BADGE_ICON: typeof ICON;
    COLOR: typeof COLOR;
    HOVER_ICON: typeof AVATAR_HOVER_ICON;
    ICON: typeof ICON;
    SIZE: typeof AVATAR_SIZE;
    VERBOSITY: typeof PRODUCT_INFO_VERBOSITY;
}

export const ProductInfo: React.FC<ProductInfoProps> & ProductInfoStatics = ({
    alias,
    aliasLabel = '',
    alignment = PRODUCT_INFO_ALIGNMENT.LEFT,
    amount,
    amountLabel = '',
    amountInfo,
    amountInfoLabel = '',
    avatar,
    backButton,
    badgeColor,
    badgeIcon,
    badges,
    badgesLabel = '',
    className,
    color,
    horizontal,
    hoverIcon,
    hoverIconLabel = '',
    hoverIconProps = {},
    icon,
    imageUrl = '',
    productNumber,
    productNumberLabel = '',
    size,
    stack,
    truncate = true,
    type,
    typeLabel = '',
    verbosity = PRODUCT_INFO_VERBOSITY.BRIEF,
    ...rest
}) => {
    deprecate(!!icon, 'ProductInfo.icon', 'Please use ProductInfo.avatar instead.');
    deprecate(!!imageUrl, 'ProductInfo.imageUrl', 'Please use ProductInfo.avatar instead.');
    deprecate(!!badgeIcon, 'ProductInfo.badgeIcon', 'Please use ProductInfo.avatar instead.');
    deprecate(!!badgeColor, 'ProductInfo.badgeColor', 'Please use ProductInfo.avatar instead.');
    deprecate(!!size, 'ProductInfo.size', 'Please use ProductInfo.avatar instead.');
    deprecate(!!hoverIcon, 'ProductInfo.hoverIcon', 'Please use ProductInfo.avatar instead.');
    deprecate(
        !!hoverIconLabel,
        'ProductInfo.hoverIconLabel',
        'Please use ProductInfo.avatar instead.',
    );
    deprecate(
        Object.keys(!!hoverIconProps).length > 0,
        'ProductInfo.hoverIconProps',
        'Please use ProductInfo.avatar instead.',
    );

    const renderKeyValue = (
        key: string,
        value: React.ReactNode,
        keyValueClassName: string,
        options: { emphasis?: boolean; faded?: boolean } = {},
    ) => {
        const showLabel = verbosity === PRODUCT_INFO_VERBOSITY.REGULAR;

        return (
            <Inline emphasis={options.emphasis} faded={options.faded} className={keyValueClassName}>
                <VisuallyHidden>{showLabel && `${key}: `}</VisuallyHidden>
                {value}{' '}
            </Inline>
        );
    };

    const renderStripe = () => {
        if (!color) return null;

        return (
            <ColorDecoration
                className="g-product-info-stripe"
                color={color}
                position={horizontal ? ColorDecoration.POSITION.LEFT : ColorDecoration.POSITION.TOP}
            />
        );
    };

    const renderButton = () => {
        if (!backButton) return null;

        return cloneElement(backButton, {
            variant: Button.VARIANT.TERTIARY,
            icon: Button.ICON.CARET_LEFT,
            hiddenLabel: Button.HIDDEN_LABEL.ALWAYS,
            fullWidth: Button.FULL_WIDTH.NEVER,
        });
    };

    const renderImage = (isInLargePlusTiers: boolean) => {
        const hideAvatar = backButton && !isInLargePlusTiers;

        if ((!avatar && !icon && !imageUrl) || hideAvatar) return null;

        if (avatar) return avatar;

        return (
            <Avatar
                icon={icon}
                imageUrl={imageUrl}
                size={size || Avatar.SIZE.SIZE_48}
                hoverIcon={hoverIcon}
                hoverIconLabel={hoverIconLabel}
                hoverIconProps={hoverIconProps}
                badgeIcon={badgeIcon}
                badgeColor={badgeColor}
            />
        );
    };

    const renderAlias = () => {
        if (!alias) return null;

        const classNames = cx('g-major', {
            'g-no-ellipsis': !truncate || alignment === PRODUCT_INFO_ALIGNMENT.RIGHT,
        });

        return renderKeyValue(aliasLabel, alias, classNames);
    };

    const renderMicro = (text: React.ReactNode, label: string, emphasis?: boolean) => {
        if (!text) return null;

        const classNames = cx('g-minor', truncate || 'g-no-ellipsis');

        return renderKeyValue(label, text, classNames, { emphasis, faded: true });
    };

    const renderAmount = () => {
        if (!amount) return null;

        return renderKeyValue(
            amountLabel,
            React.Children.map(amount, (singleAmount) => [singleAmount, ' ']),
            'g-product-info-amount',
        );
    };

    const renderBadge = () => {
        if (!badges) return null;

        return renderKeyValue(
            badgesLabel,
            React.Children.map(badges, (badge) => [badge, ' ']),
            'g-product-info-badge',
        );
    };

    const classNames = cx(
        'g-product-info',
        horizontal && 'g-product-info-horizontal',
        color && 'g-product-info-striped',
        badges && 'g-product-info-badged',
        amount && 'g-product-info-totaled',
        stack && 'g-product-info-direction-column',
        alignment === PRODUCT_INFO_ALIGNMENT.RIGHT && 'g-product-info-right',
        className,
    );

    return (
        <MatchMedia min={MatchMedia.TIER.LG}>
            {({ isMatching }) => (
                <Inline className={classNames}>
                    {renderButton()}
                    <Inline {...omitProps(rest, ProductInfo)} className="g-product-info-data">
                        {renderAlias()}
                        {renderMicro(type, typeLabel, true)}
                        {renderMicro(productNumber, productNumberLabel)}
                        {renderAmount()}
                        {renderMicro(amountInfo, amountInfoLabel)}
                        {renderBadge()}
                    </Inline>
                    {renderImage(isMatching)}
                    {renderStripe()}
                </Inline>
            )}
        </MatchMedia>
    );
};

ProductInfo.ALIGNMENT = PRODUCT_INFO_ALIGNMENT;
ProductInfo.BADGE_COLOR = Avatar.BADGE_COLOR;
ProductInfo.BADGE_ICON = Avatar.BADGE_ICON;
ProductInfo.COLOR = COLOR;
ProductInfo.HOVER_ICON = Avatar.HOVER_ICON;
ProductInfo.ICON = Avatar.ICON;
ProductInfo.SIZE = Avatar.SIZE;
ProductInfo.VERBOSITY = PRODUCT_INFO_VERBOSITY;

ProductInfo.displayName = 'ProductInfo';
ProductInfo.propTypes = {
    color: PropTypes.oneOf(Object.values(ProductInfo.COLOR)),
    horizontal: PropTypes.bool,
    truncate: PropTypes.bool,
    stack: PropTypes.bool,
    icon: PropTypes.oneOf(Object.values(Avatar.ICON)), // DEPRECATED
    badgeIcon: PropTypes.oneOf(Object.values(ProductInfo.BADGE_ICON)), // DEPRECATED
    badgeColor: PropTypes.oneOf(Object.values(ProductInfo.BADGE_COLOR)), // DEPRECATED
    imageUrl: PropTypes.string, // DEPRECATED
    size: PropTypes.oneOf(Object.values(Avatar.SIZE)), // DEPRECATED
    type: PropTypes.node,
    typeLabel: requiredIf(
        PropTypes.string,
        ({ type, verbosity }) => type && verbosity && verbosity !== PRODUCT_INFO_VERBOSITY.BRIEF,
    ),
    alias: PropTypes.node,
    aliasLabel: requiredIf(
        PropTypes.string,
        ({ alias, verbosity }) => alias && verbosity && verbosity !== PRODUCT_INFO_VERBOSITY.BRIEF,
    ),
    productNumber: PropTypes.node,
    productNumberLabel: requiredIf(
        PropTypes.string,
        ({ productNumber, verbosity }) =>
            productNumber && verbosity && verbosity !== PRODUCT_INFO_VERBOSITY.BRIEF,
    ),
    amount: PropTypes.node,
    amountLabel: requiredIf(
        PropTypes.string,
        ({ amount, verbosity }) =>
            amount && verbosity && verbosity !== PRODUCT_INFO_VERBOSITY.BRIEF,
    ),
    amountInfo: PropTypes.node,
    amountInfoLabel: requiredIf(
        PropTypes.string,
        ({ amountInfo, verbosity }) =>
            amountInfo && verbosity && verbosity !== PRODUCT_INFO_VERBOSITY.BRIEF,
    ),
    // @ts-expect-error classic PropTypes problem
    badges: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
    badgesLabel: requiredIf(
        PropTypes.string,
        ({ badges, verbosity }) =>
            badges && verbosity && verbosity !== PRODUCT_INFO_VERBOSITY.BRIEF,
    ),
    hoverIcon: PropTypes.oneOf(Object.values(Avatar.HOVER_ICON) as AVATAR_HOVER_ICON[]), // DEPRECATED
    hoverIconLabel: requiredIf(
        PropTypes.string,
        ({ hoverIcon, verbosity }) =>
            hoverIcon && verbosity && verbosity !== PRODUCT_INFO_VERBOSITY.BRIEF,
    ), // DEPRECATED
    hoverIconProps: PropTypes.shape({}), // DEPRECATED
    avatar: elementOfType([Avatar, Button]),
    verbosity: PropTypes.oneOf(Object.values(PRODUCT_INFO_VERBOSITY)),
    backButton: elementOfType([Button]),
    alignment: PropTypes.oneOf(Object.values(PRODUCT_INFO_ALIGNMENT)),
    className: PropTypes.string,
};
